// FocusTimerStart.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faVideo, faShare, faGlobe, faList12, faGears, faTimes } from '@fortawesome/free-solid-svg-icons';

import pomodoroData from './Pomodoro';

const gentlePianoMusic = process.env.PUBLIC_URL + '/assets/sounds/cheerful-whistling.mp3';

const FocusTimerStart = () => {
  const navigate = useNavigate();
  const authState = useSelector(state => state.authReducer);
  const [userName, setUserName] = useState(authState.user.name);
  const [tasks, setTasks] = useState([]);
  const [fetchData] = useFetch();
  const [currentTaskTitle, setCurrentTaskTitle] = useState('');
  const [currentTaskId, setCurrentTaskId] = useState('');
  const [showTaskList, setShowTaskList] = useState(false);
  const [showPreference, setShowPreference] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [error, setError] = useState('');

  const [focusTime, setFocusTime] = useState(25);
  const [shortBreakDuration, setShortBreakDuration] = useState(5);
  const [longBreakDuration, setLongBreakDuration] = useState(25);
  const [shortBreakCount, setShortBreakCount] = useState(12);
  const [longBreakCount, setLongBreakCount] = useState(1);

  const [initialTime, setInitialTime] = useState(focusTime);
  const [time, setTime] = useState(initialTime);
  const [isTimeForBreak, setIsTimeForBreak] = useState(false);
  const [timerOn, setTimerOn] = useState(false);
  const [showStopButtons, setShowStopButtons] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const [audio] = useState(new Audio(gentlePianoMusic));

  const sortedTasks = [...tasks].sort((a, b) => new Date(b.taskDate) - new Date(a.taskDate));

  const fetchTasks = useCallback(() => {
    const config = {
      url: "/tasks/myfocustimertasks",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id }
    };
    fetchData(config, { showSuccessToast: false }).then(data => setTasks(data.tasks));
  }, [authState.token, fetchData, authState.user._id]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchTasks();
  }, [authState.isLoggedIn, fetchTasks]);

  useEffect(() => {
    let interval;
    if (timerOn) {
      interval = setInterval(() => setTime(prev => prev - 1), 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerOn]);

  useEffect(() => {
    if (timerOn) audio.play(); else audio.pause();
    if (time === 0) {
      setTimerOn(false);
      setIsTimeForBreak(true);
      audio.pause();
      audio.currentTime = 0;
    }
  }, [timerOn, audio, time]);

  const handleShowTaskList = () => {
    setShowTaskList(!showTaskList);
    if (tasks.length === 0) fetchTasks();
  };

  const handleTaskClick = (title, id) => {
    setCurrentTaskTitle(title);
    setCurrentTaskId(id);
    setShowTaskList(false);
    setStartTime(new Date());
  };

  const handleBackToHome = () => navigate('/focus-timer-home');

  const startTimer = () => { setTime(initialTime); setTimerOn(true); };
  const pauseTimer = () => setTimerOn(false);
  const stopTimer = () => { setShowStopButtons(true); setTimerOn(false); setTime(initialTime); setDisableButtons(true); audio.pause(); audio.currentTime = 0; };

  const handleSaveData = () => {
    return;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const focusData = {
      userId: authState.user._id,
      taskId: currentTaskId,
      startTime: startTime,
      endTime: endTime,
      timeSpent: end - start,
      pomodoroTime: time,
    };
    saveFocusData(focusData);
  };

  const saveFocusData = (focusData) => {
    const config = { url: "/focustimer", method: "post", data: { focusData }, headers: { Authorization: authState.token } };
    fetchData(config)
      .then(() => navigate("/focus-timer-home"))
      .catch(err => setError("Error saving focus timer"));
  };

  const handleShowPreference = () => setShowPreference(!showPreference);
  const handleSavePreference = () => setInitialTime(focusTime);

  useEffect(() => setInitialTime(focusTime), [focusTime]);

  const formatTime = t => `${Math.floor(t/60).toString().padStart(2,'0')}:${(t%60).toString().padStart(2,'0')}`;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center py-4 px-2 md:px-6">
      <h2 className='text-2xl md:text-3xl font-bold text-blue-400 text-center mb-4'>Focus Timer</h2>

      {/* Controls */}
      <div className="grid grid-cols-4 gap-2 place-items-center">
        <button className="w-20 p-2 bg-blue-700 rounded" onClick={handleShowTaskList}><FontAwesomeIcon icon={faList12} /></button>
        <button className="w-20 p-2 bg-blue-700 rounded" onClick={handleShowPreference}><FontAwesomeIcon icon={faGears} /></button>
        <button className="w-20 p-2 bg-gray-700 rounded" disabled><FontAwesomeIcon icon={faGlobe} /></button>
        <button className="w-20 p-2 bg-red-700 rounded" onClick={handleBackToHome}><FontAwesomeIcon icon={faTimes} /></button>
      </div>

      {/* Task List Modal */}
      {showTaskList && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="w-full md:w-7/12 max-h-[80vh] overflow-y-auto bg-gray-900 text-white rounded p-4">
            <h3 className="text-lg font-semibold mb-2">Select Your Task to Focus On</h3>
            {sortedTasks.length > 0 ? (
              sortedTasks.map((task, idx) => (
                <p key={task._id} onClick={() => handleTaskClick(task.title, task._id)} className="cursor-pointer border-b border-gray-700 py-2">
                  <strong>{idx + 1}. </strong>{task.title}
                </p>
              ))
            ) : (
              <div className="text-center text-gray-400 py-20">There is no Task to Focus!!</div>
            )}
            <button className="mt-4 p-2 bg-red-700 rounded" onClick={()=>setShowTaskList(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreference && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="w-full md:w-7/12 max-h-[80vh] overflow-y-auto bg-gray-900 text-white rounded p-4 space-y-4">
            <div>
              <label className="block mb-1">Focus Time (minutes)</label>
              <input type="number" value={focusTime} onChange={e => setFocusTime(e.target.value)} min="10" max="115" step="5" className="w-full p-2 rounded bg-gray-800 text-white text-center"/>
            </div>
            <div>
              <label className="block mb-1">Short Break Duration (minutes)</label>
              <input type="number" value={shortBreakDuration} onChange={e => setShortBreakDuration(e.target.value)} min="5" max="10" step="5" className="w-full p-2 rounded bg-gray-800 text-white text-center"/>
            </div>
            <div>
              <label className="block mb-1">Max Number of Short Breaks</label>
              <input type="number" value={shortBreakCount} onChange={e => setShortBreakCount(e.target.value)} min="1" max="40" step="5" className="w-full p-2 rounded bg-gray-800 text-white text-center"/>
            </div>
            <div>
              <label className="block mb-1">Long Break Duration (minutes)</label>
              <input type="number" value={longBreakDuration} onChange={e => setLongBreakDuration(e.target.value)} min="25" max="120" step="10" className="w-full p-2 rounded bg-gray-800 text-white text-center"/>
            </div>
            <div>
              <label className="block mb-1">Max Number of Long Breaks</label>
              <input type="number" value={longBreakCount} onChange={e => setLongBreakCount(e.target.value)} min="1" max="40" step="5" className="w-full p-2 rounded bg-gray-800 text-white text-center"/>
            </div>
            <div className="flex justify-end gap-2">
              <button className="p-2 bg-green-600 rounded" onClick={handleSavePreference}>Save</button>
              <button className="p-2 bg-red-600 rounded" onClick={()=>setShowPreference(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Current Task */}
      <div className="text-center text-xl lg:text-2xl font-semibold mt-4">{currentTaskTitle}</div>

      {/* Timer Display */}
      <div className="text-center text-4xl lg:text-6xl font-mono mb-8">{formatTime(time)}</div>

      {/* Timer Buttons */}
      <div className="grid grid-cols-3 gap-2 mt-2 place-items-center">
        <button onClick={startTimer} disabled={timerOn || showStopButtons} className="px-4 py-2 bg-blue-600 rounded disabled:opacity-50">Start</button>
        <button onClick={stopTimer} disabled={!timerOn} className="px-4 py-2 bg-orange-600 rounded disabled:opacity-50">Stop for Short Break</button>
        <button onClick={stopTimer} disabled={!timerOn} className="px-4 py-2 bg-purple-600 rounded disabled:opacity-50">Stop for Long Break</button>
      </div>

      {/* Additional Buttons */}
      {showStopButtons && (
        <div className="flex flex-col lg:flex-row justify-center gap-2 mt-4">
          <button className="px-4 py-2 bg-orange-700 rounded" onClick={handleSaveData}>Stop & Do 5Whys Analysis</button>
          <button className="px-4 py-2 bg-green-700 rounded" onClick={handleBackToHome}>Back To Home</button>
        </div>
      )}

      {/* Pomodoro Suggestions */}
      <div className="w-full max-w-4xl mt-8 p-4 bg-gray-900 border border-blue-700 rounded">
        <h3 className="text-xl font-semibold text-blue-400 mb-4">Suggestions for Activities in Short & Long Break</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pomodoroData.map((pomodoro, idx) => (
            <div key={idx} className="bg-gray-800 p-3 rounded space-y-2">
              <h4 className="font-semibold text-blue-300">{pomodoro.type}</h4>
              {pomodoro.activities.map((activity, aIdx) => (
                <div key={aIdx} className={`flex items-center p-1 rounded ${aIdx % 2 === 0 ? 'bg-gray-700' : 'bg-gray-600'}`}>
                  <span className="mr-2"><i className={activity.icon}></i></span>
                  <p>{activity.activity}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-gray-900 border-t border-blue-700 mt-8 py-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between px-3 gap-3 text-sm text-gray-400">
          <div>© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4">
            <Link to="/about-us" className="hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FocusTimerStart;
